import {
  BellIcon,
  CalendarIcon,
  CreditCardIcon,
  DocumentTextIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { useMember } from '../../contexts/MemberContext';
import { payments as stubPayments } from '../../utils/stubData';
import Sidebar from '../common/Sidebar';

export default function MemberDashboard() {
  const { memberships, loading, error, fetchMemberships } = useMember();

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  // Get primary membership (first active, or first membership if none active)
  const primaryMembership =
    memberships.find(m => m.status === 'active' && m.isPrimary) ||
    memberships.find(m => m.status === 'active') ||
    memberships[0] ||
    null;

  // Count other memberships
  const otherMembershipsCount = memberships.length > 1 ? memberships.length - 1 : 0;

  // Calculate renewal status
  const getRenewalStatus = validUntil => {
    const today = new Date();
    const renewalDate = new Date(validUntil);
    const daysUntil = Math.ceil((renewalDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      return {
        status: 'expired',
        color: 'text-red-400',
        bgColor: 'bg-red-900/20',
        message: 'Expired',
        daysUntil: daysUntil,
      };
    } else if (daysUntil <= 30) {
      return {
        status: 'urgent',
        color: 'text-red-400',
        bgColor: 'bg-red-900/20',
        message: `${daysUntil} days remaining`,
        daysUntil: daysUntil,
      };
    } else if (daysUntil <= 60) {
      return {
        status: 'soon',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-900/20',
        message: `${daysUntil} days remaining`,
        daysUntil: daysUntil,
      };
    } else {
      return {
        status: 'active',
        color: 'text-green-400',
        bgColor: 'bg-green-900/20',
        message: `${daysUntil} days remaining`,
        daysUntil: daysUntil,
      };
    }
  };

  // Get recent payments for primary membership (stub data for demo)
  const recentPayments = primaryMembership
    ? stubPayments
        .filter(p => p.membershipNumber === primaryMembership.number && p.status === 'completed')
        .slice(0, 3)
    : [];

  const renewalStatus = primaryMembership ? getRenewalStatus(primaryMembership.validUntil) : null;
  const needsRenewal =
    renewalStatus && (renewalStatus.status === 'urgent' || renewalStatus.status === 'soon');

  return (
    <div className="min-h-screen bg-hdlcc-black">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64">
          <div className="p-8">
            <div className="bg-gradient-to-br from-hdlcc-darkGray via-hdlcc-darkGray to-hdlcc-mediumGray text-white p-8 rounded-2xl shadow-elevation-2 border border-hdlcc-mediumGray/30">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-hdlcc-textPrimary tracking-tight">
                    My Membership
                  </h2>
                  <p className="text-hdlcc-textSecondary mt-1">
                    {primaryMembership
                      ? `Membership #${primaryMembership.number}`
                      : 'Welcome to your member portal'}
                  </p>
                </div>
              </div>

              {loading && (
                <div data-testid="loading-memberships" className="text-center py-12">
                  <div className="inline-flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hdlcc-primary"></div>
                    <p className="text-hdlcc-textSecondary">Loading membership details...</p>
                  </div>
                </div>
              )}

              {error && (
                <div
                  data-testid="error-message"
                  className="bg-hdlcc-error/10 border border-hdlcc-error/30 text-hdlcc-error p-6 rounded-xl mb-6 shadow-elevation-1"
                >
                  <p className="font-medium">{error}</p>
                  <button
                    onClick={fetchMemberships}
                    className="mt-3 text-white bg-hdlcc-error px-5 py-2.5 rounded-lg hover:bg-hdlcc-errorDark transition-all duration-200 font-medium shadow-elevation-1"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && !primaryMembership && (
                <div data-testid="empty-memberships" className="text-center py-12">
                  <p className="text-hdlcc-textSecondary mb-6 text-lg">No membership found</p>
                  <Link
                    to="/membership/register"
                    className="inline-flex items-center bg-gradient-to-r from-hdlcc-primary to-hdlcc-primaryLight text-white px-6 py-3 rounded-xl hover:shadow-glow-primary transition-all duration-200 font-medium"
                  >
                    Register New Membership
                  </Link>
                </div>
              )}

              {!loading && !error && primaryMembership && (
                <>
                  {/* Renewal Alert */}
                  {needsRenewal && (
                    <div className="bg-gradient-to-r from-hdlcc-warning/15 to-hdlcc-warning/5 border border-hdlcc-warning/40 p-6 rounded-xl mb-8 shadow-elevation-1">
                      <div className="flex items-start">
                        <BellIcon className="w-7 h-7 text-hdlcc-warning mr-4 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-hdlcc-warning mb-2">
                            Renewal Reminder
                          </h3>
                          <p className="text-hdlcc-textSecondary mb-4 leading-relaxed">
                            Your membership is due for renewal soon -{' '}
                            <span className="text-hdlcc-warning font-semibold">
                              {renewalStatus.message}
                            </span>
                          </p>
                          <Link
                            to="/renewal"
                            className="inline-flex items-center bg-gradient-to-r from-hdlcc-warning to-hdlcc-warningDark text-white px-5 py-2.5 rounded-lg hover:shadow-lg transition-all duration-200 font-medium"
                          >
                            Renew Now
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Membership Details Card */}
                  <div className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 p-8 rounded-xl shadow-elevation-1 mb-8">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-bold text-hdlcc-textPrimary">
                        Membership Details
                      </h3>
                      <span
                        className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                          primaryMembership.status === 'active'
                            ? 'bg-hdlcc-success/20 border-hdlcc-success/40 text-hdlcc-success'
                            : 'bg-hdlcc-error/20 border-hdlcc-error/40 text-hdlcc-error'
                        }`}
                      >
                        {primaryMembership.status === 'active' ? 'Active' : 'Expired'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-hdlcc-textTertiary mb-1.5 font-medium">
                          Membership Number
                        </p>
                        <p className="text-lg font-bold text-hdlcc-textPrimary">
                          {primaryMembership.number}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-hdlcc-textTertiary mb-1.5 font-medium">
                          Membership Type
                        </p>
                        <p className="text-lg font-bold text-hdlcc-textPrimary">
                          {primaryMembership.type}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-hdlcc-textTertiary mb-1.5 font-medium">
                          Valid Until
                        </p>
                        <p className="text-lg font-bold text-hdlcc-textPrimary">
                          {new Date(primaryMembership.validUntil).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-hdlcc-textTertiary mb-1.5 font-medium">Status</p>
                        <p
                          className={`text-lg font-bold ${
                            renewalStatus.status === 'active'
                              ? 'text-hdlcc-success'
                              : renewalStatus.status === 'soon'
                                ? 'text-hdlcc-warning'
                                : 'text-hdlcc-error'
                          }`}
                        >
                          {renewalStatus.message}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-hdlcc-textTertiary mb-1.5 font-medium">
                          Member Since
                        </p>
                        <p className="text-lg font-bold text-hdlcc-textPrimary">
                          {new Date(primaryMembership.joinedDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-hdlcc-textTertiary mb-1.5 font-medium">
                          Contact
                        </p>
                        <p className="text-lg font-bold text-hdlcc-textPrimary">
                          {primaryMembership.contact.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
                    <div className="bg-gradient-to-br from-hdlcc-mediumGray to-hdlcc-gray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-200 group">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-hdlcc-textTertiary text-sm font-semibold">
                          Days Remaining
                        </h4>
                        <CalendarIcon className="w-6 h-6 text-hdlcc-accent group-hover:scale-110 transition-transform" />
                      </div>
                      <p
                        className={`text-4xl font-bold mt-2 ${
                          renewalStatus.status === 'active'
                            ? 'text-hdlcc-success'
                            : renewalStatus.status === 'soon'
                              ? 'text-hdlcc-warning'
                              : 'text-hdlcc-error'
                        }`}
                      >
                        {renewalStatus.daysUntil > 0 ? renewalStatus.daysUntil : 0}
                      </p>
                      {needsRenewal && (
                        <Link
                          to="/renewal"
                          className="text-hdlcc-primary hover:text-hdlcc-primaryLight text-sm mt-2 inline-block font-medium transition-colors"
                        >
                          Renew Now →
                        </Link>
                      )}
                    </div>

                    <div className="bg-gradient-to-br from-hdlcc-mediumGray to-hdlcc-gray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-200 group">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-hdlcc-textTertiary text-sm font-semibold">Documents</h4>
                        <DocumentTextIcon className="w-6 h-6 text-hdlcc-accent group-hover:scale-110 transition-transform" />
                      </div>
                      <p className="text-4xl font-bold mt-2 text-hdlcc-textPrimary">8</p>
                      <Link
                        to="/magazine"
                        className="text-hdlcc-primary hover:text-hdlcc-primaryLight text-sm mt-2 inline-block font-medium transition-colors"
                      >
                        View Magazines →
                      </Link>
                    </div>

                    <div className="bg-gradient-to-br from-hdlcc-mediumGray to-hdlcc-gray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-200 group">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-hdlcc-textTertiary text-sm font-semibold">
                          Total Paid (2025)
                        </h4>
                        <CreditCardIcon className="w-6 h-6 text-hdlcc-success group-hover:scale-110 transition-transform" />
                      </div>
                      <p className="text-4xl font-bold mt-2 text-hdlcc-textPrimary">
                        £
                        {recentPayments
                          .filter(p => p.year === 2025)
                          .reduce((sum, p) => sum + p.amount, 0)
                          .toFixed(2)}
                      </p>
                      <Link
                        to="/billing"
                        className="text-hdlcc-primary hover:text-hdlcc-primaryLight text-sm mt-2 inline-block font-medium transition-colors"
                      >
                        View History →
                      </Link>
                    </div>

                    {otherMembershipsCount > 0 && (
                      <div className="bg-gradient-to-br from-hdlcc-mediumGray to-hdlcc-gray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-all duration-200 group">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-hdlcc-textTertiary text-sm font-semibold">
                            Other Memberships
                          </h4>
                          <UserGroupIcon className="w-6 h-6 text-purple-400 group-hover:scale-110 transition-transform" />
                        </div>
                        <p className="text-4xl font-bold mt-2 text-hdlcc-textPrimary">
                          {otherMembershipsCount}
                        </p>
                        <Link
                          to="/manage-others"
                          className="text-hdlcc-primary hover:text-hdlcc-primaryLight text-sm mt-2 inline-block font-medium transition-colors"
                        >
                          Manage Others →
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Recent Activity */}
                  {recentPayments.length > 0 && (
                    <div className="bg-hdlcc-mediumGray border border-hdlcc-lightGray/20 p-6 rounded-xl shadow-elevation-1">
                      <h3 className="text-xl font-bold mb-6 text-hdlcc-textPrimary">
                        Recent Payments
                      </h3>
                      <div className="space-y-2">
                        {recentPayments.map(payment => (
                          <div
                            key={payment.id}
                            className="flex items-center justify-between p-4 bg-hdlcc-gray/50 rounded-lg border border-hdlcc-lightGray/10 hover:bg-hdlcc-gray transition-all duration-200"
                          >
                            <div>
                              <p className="font-semibold text-hdlcc-textPrimary">
                                Membership Fee - {payment.year}
                              </p>
                              <p className="text-sm text-hdlcc-textTertiary mt-0.5">
                                {new Date(payment.paymentDate).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-hdlcc-textPrimary text-lg">
                                £{payment.amount.toFixed(2)}
                              </p>
                              <p className="text-xs text-hdlcc-textTertiary mt-0.5">
                                {payment.receiptNumber}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Link
                        to="/billing"
                        className="inline-flex items-center mt-6 text-hdlcc-primary hover:text-hdlcc-primaryLight text-sm font-medium transition-colors group"
                      >
                        View All Payments
                        <span className="ml-1 group-hover:translate-x-1 transition-transform">
                          →
                        </span>
                      </Link>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
